<template>
  <div class="fl-select" :class="{ 'is-open': open }">
    <button
      type="button"
      class="fl-select__trigger"
      :class="{ 'is-empty': !selectedOption }"
      aria-haspopup="listbox"
      :aria-expanded="open ? 'true' : 'false'"
      @click="toggle"
      @keydown.down.prevent="openPanel"
    >
      <span>{{ selectedOption ? selectedOption.label : placeholder }}</span>
      <small v-if="selectedOption && selectedOption.hint">{{ selectedOption.hint }}</small>
    </button>

    <div class="fl-select__panel" :class="{ 'is-up': dropUp }" v-if="open">
      <input
        v-if="normalisedOptions.length > searchThreshold"
        ref="search"
        type="text"
        class="fl-select__search"
        v-model="query"
        :placeholder="'Search ' + normalisedOptions.length + '…'"
        :aria-label="'Search ' + placeholder"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="chooseActive"
        @keydown.esc.prevent="close"
      >

      <ul class="fl-select__list" role="listbox" ref="list">
        <li
          v-for="(option, index) of visibleOptions"
          :key="'o' + option.value"
          role="option"
          :aria-selected="option.value === modelValue ? 'true' : 'false'"
          :class="{ 'is-on': option.value === modelValue, 'is-active': index === activeIndex }"
          @click="choose(option.value)"
          @mousemove="activeIndex = index"
        >
          <b>{{ option.label }}</b>
          <small v-if="option.hint">{{ option.hint }}</small>
        </li>
        <li class="fl-select__empty" v-if="!visibleOptions.length">
          {{ normalisedOptions.length ? 'Nothing matches “' + query + '”.' : emptyLabel }}
        </li>
      </ul>

      <button type="button" class="fl-select__clear" v-if="allowClear && modelValue" @click="choose('')">
        Clear selection
      </button>
    </div>
  </div>
</template>

<script>
  import { defineComponent } from 'vue'

  // Search box plus the list's own max-height, used to decide whether the panel still fits below.
  const PANEL_MAX_HEIGHT = 285

  /**
   * Single-select with a search box, in the augmented profile's `fl-` design language.
   *
   * Replaces the native `<select>` elements in the wizard: a country list is ~250 entries and a
   * technology taxonomy can be hundreds, which is unusable without typing. Accepts either option
   * shape the page already has — `{ value, label, hint }` or the taxonomy `{ id, name, parent }` —
   * so call sites do not need an adapter each.
   */
  export default defineComponent({
    name: 'FoodleapSelect',
    props: {
      modelValue: {
        type: [String, Number],
        default: '',
      },
      options: {
        type: Array,
        default: () => [],
      },
      placeholder: {
        type: String,
        default: 'Choose…',
      },
      emptyLabel: {
        type: String,
        default: 'Nothing is configured for this ecosystem yet.',
      },
      /** Above this many options the panel gets a search box. */
      searchThreshold: {
        type: Number,
        default: 8,
      },
      allowClear: {
        type: Boolean,
        default: true,
      },
    },
    emits: ['update:modelValue'],
    data () {
      return {
        open: false,
        query: '',
        activeIndex: 0,
        dropUp: false,
      }
    },
    computed: {
      normalisedOptions () {
        return (this.options || []).map(option => ({
          value: option.value !== undefined && option.value !== null ? option.value : option.id,
          label: option.label !== undefined && option.label !== null ? option.label : option.name,
          hint: option.hint || option.parent || '',
        }))
      },
      selectedOption () {
        if (this.modelValue === '' || this.modelValue === null || this.modelValue === undefined) {
          return null
        }

        return this.normalisedOptions.find(option => option.value === this.modelValue) || null
      },
      visibleOptions () {
        const query = this.query.trim().toLowerCase()

        if (!query) {
          return this.normalisedOptions
        }

        return this.normalisedOptions
          .filter(option => `${option.label} ${option.hint}`.toLowerCase().includes(query))
      },
    },
    beforeUnmount () {
      document.removeEventListener('mousedown', this.onDocumentClick)
    },
    methods: {
      toggle () {
        return this.open ? this.close() : this.openPanel()
      },
      openPanel () {
        // The wizard body is `overflow: auto`, so a panel opening downward near the bottom gets
        // clipped. Flip it above the trigger when there is not enough room below.
        const rect = this.$el.getBoundingClientRect()

        this.dropUp = (window.innerHeight - rect.bottom) < PANEL_MAX_HEIGHT &&
          rect.top > PANEL_MAX_HEIGHT

        this.open = true
        this.query = ''
        this.activeIndex = Math.max(
          0,
          this.normalisedOptions.findIndex(option => option.value === this.modelValue),
        )

        document.addEventListener('mousedown', this.onDocumentClick)

        this.$nextTick(() => {
          if (this.$refs.search) {
            this.$refs.search.focus()
          }
        })
      },
      close () {
        this.open = false
        document.removeEventListener('mousedown', this.onDocumentClick)
      },
      onDocumentClick (event) {
        if (!this.$el.contains(event.target)) {
          this.close()
        }
      },
      choose (value) {
        this.$emit('update:modelValue', value)
        this.close()
      },
      chooseActive () {
        const option = this.visibleOptions[this.activeIndex]

        if (option) {
          this.choose(option.value)
        }
      },
      move (delta) {
        const count = this.visibleOptions.length

        if (!count) {
          return
        }

        // Wraps, so holding one arrow key cannot dead-end at either edge.
        this.activeIndex = (this.activeIndex + delta + count) % count
        this.scrollActiveIntoView()
      },
      scrollActiveIntoView () {
        this.$nextTick(() => {
          const list = this.$refs.list

          if (!list) {
            return
          }

          const item = list.children[this.activeIndex]

          if (item && item.scrollIntoView) {
            item.scrollIntoView({ block: 'nearest' })
          }
        })
      },
    },
    watch: {
      // Filtering can leave the highlight past the end of a shorter list.
      query () {
        this.activeIndex = 0
      },
    },
  })
</script>

<style lang="scss" scoped>
  .fl-select {
    position: relative;
  }

  .fl-select__trigger {
    display: block;
    width: 100%;
    border: 1px solid var(--fl-line, #dfe8df);
    background-color: #fff;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' fill='none' stroke='%236f7d78' stroke-width='1.4' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    background-size: 10px 6px;
    padding: 12px 32px 12px 12px;
    font-family: inherit;
    font-size: 13px;
    color: inherit;
    text-align: left;
    cursor: pointer;

    > span {
      display: block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    small {
      display: block;
      margin-top: 4px;
      font-size: 10px;
      color: var(--fl-muted, #6f7d78);
    }

    &.is-empty > span {
      color: var(--fl-muted, #6f7d78);
    }

    &:hover,
    .is-open & {
      border-color: var(--fl-green, #5cc61e);
    }
  }

  .fl-select__panel {
    position: absolute;
    z-index: 5;
    top: calc(100% - 1px);
    left: 0;
    right: 0;
    border: 1px solid var(--fl-green, #5cc61e);
    background: #fff;
    box-shadow: 0 8px 24px rgba(51, 65, 62, .12);

    &.is-up {
      top: auto;
      bottom: calc(100% - 1px);
      box-shadow: 0 -8px 24px rgba(51, 65, 62, .12);
    }
  }

  .fl-select__search {
    width: 100%;
    border: 0;
    border-bottom: 1px solid var(--fl-line, #dfe8df);
    padding: 12px;
    font-family: inherit;
    font-size: 13px;
    color: inherit;

    &:focus {
      outline: none;
    }
  }

  .fl-select__list {
    max-height: 240px;
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      padding: 8px 12px;
      font-size: 12px;
      cursor: pointer;

      b {
        font-weight: 400;
      }

      small {
        display: block;
        margin-top: 4px;
        font-size: 10px;
        color: var(--fl-muted, #6f7d78);
      }

      &.is-active {
        background: var(--fl-pale, #f4f8f3);
      }

      &.is-on {
        background: var(--fl-mint, #eaf6e6);

        b {
          font-weight: 500;
        }
      }
    }
  }

  .fl-select__empty {
    color: var(--fl-muted, #6f7d78);
    cursor: default;
  }

  .fl-select__clear {
    display: block;
    width: 100%;
    border: 0;
    border-top: 1px solid var(--fl-line, #dfe8df);
    background: none;
    padding: 8px 12px;
    font-family: inherit;
    font-size: 11px;
    text-align: left;
    color: var(--fl-coral, #ef374e);
    cursor: pointer;
  }
</style>
